import type { GameDirectory } from '@shared/types/game';

import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const GAME_DIRECTORIES: GameDirectory[] = [
  { name: 'Derby_of_Fortune', gameid: '5e31238e-5637-4da6-99f1-81098f8c7f0d' },
  { name: 'Tropical_Fruity_Cash', gameid: 'fae9bc4a-4ab2-45df-bfaa-3bedb2881cef' },
  { name: 'Stallion_Fantasy', gameid: '0f8eb4d3-fd4c-4a6e-bdad-07538fc16811' },
  { name: 'Lord_of_the_Atlantis', gameid: '23779171-8f0b-4d91-9804-80a6ebb7fa2b' },
  { name: 'Modubloxx_Tiger_Era', gameid: '3bd7af9c-2c7f-4b8d-bc52-4eaa7190c219' },
  { name: 'Marshmallow_Bonanza', gameid: '5c4a9e12-3bdf-4f19-9321-09abf05fef43' },
  { name: 'Cash&Chips_Vegas_Kings', gameid: '9e418ac5-6d13-4e41-92fa-a8bb9be06312' },
  { name: 'Goldwink_Discovery', gameid: 'b7e2c3f4-8ad1-4c2e-b9f8-4be3d27d0a77' },
  { name: 'Pagoda_Treasure88', gameid: 'f0aa27e6-95be-4d5e-881a-2b8c80e490cf' },
  { name: 'Rise_Riches', gameid: 'a4e5c5bc-7f62-4855-8dbe-071412f6a9d9' },
  { name: 'Pop_the_Fisherman', gameid: '6c5c6a6a-1a4f-4e3b-b0d5-7a2a2c3c9d81' },
  { name: 'Toros_Vault', gameid: '9f2a8b72-3f6e-4c8c-9d1a-5b7f4e2c6a13' },
] as const;

const GAME_MODULES: Record<string, () => Promise<unknown>> = {
  Derby_of_Fortune: () => import('./games/Derby_of_Fortune/assets/js/reels-entry'),
  Tropical_Fruity_Cash: () => import('./games/Tropical_Fruity_Cash/assets/js/reels-entry'),
  Stallion_Fantasy: () => import('./games/Stallion_Fantasy/assets/js/reels-entry'),
  Lord_of_the_Atlantis: () => import('./games/Lord_of_the_Atlantis/assets/js/reels-entry'),
  Modubloxx_Tiger_Era: () => import('./games/Modubloxx_Tiger_Era/assets/js/reels-entry'),
  Marshmallow_Bonanza: () => import('./games/Marshmallow_Bonanza/assets/js/reels-entry'),
  'Cash&Chips_Vegas_Kings': () => import('./games/Cash&Chips_Vegas_Kings/assets/js/reels-entry'),
  Goldwink_Discovery: () => import('./games/Goldwink_Discovery/assets/js/reels-entry'),
  Pagoda_Treasure88: () => import('./games/Pagoda_Treasure88/assets/js/reels-entry'),
  Rise_Riches: () => import('./games/Rise_Riches/assets/js/reels-entry'),
  Pop_the_Fisherman: () => import('./games/Pop_the_Fisherman/assets/js/reels-entry'),
  Toros_Vault: () => import('./games/Toros_Vault/assets/js/reels-entry'),
};

function parseUrlParams(): { gameid: string | null; accountid: string | null; token: string | null } {
  const urlParams = new URLSearchParams(window.location.search);

  const getParam = (paramName: string): string | null => {
    const lower = paramName.toLowerCase();
    for (const [key, value] of urlParams.entries()) {
      if (key.toLowerCase() === lower) return value;
    }
    return null;
  };

  return {
    gameid: getParam('gameid') ?? getParam('game_id') ?? getParam('gameId'),
    accountid: getParam('accountid') ?? getParam('account_id') ?? getParam('accountId'),
    token: getParam('token') ?? getParam('access_token') ?? getParam('accessToken'),
  };
}

function getGameConfig(gameId: string | null): GameDirectory | null {
  if (!gameId) return null;
  return GAME_DIRECTORIES.find((d) => d.gameid === gameId) ?? null;
}

async function loadGame(gameName: string): Promise<void> {
  const loader = GAME_MODULES[gameName];
  if (!loader) {
    console.error(`No module loader for game: ${gameName}`);
    return;
  }
  await loader();
}

let isOfflineSwalOpen = false;
const REFRESH_DELAY_MS = 3_000;

function handleOnline(): void {
  if (!isOfflineSwalOpen) return;
  isOfflineSwalOpen = false;
  Swal.close();
  Swal.fire({
    title: "You're back online",
    text: 'Refreshing the page in 3 s…',
    icon: 'success',
    allowOutsideClick: false,
    showConfirmButton: false,
    timer: REFRESH_DELAY_MS,
    timerProgressBar: true,
  });
  setTimeout(() => window.location.reload(), REFRESH_DELAY_MS);
}

function handleOffline(): void {
  if (isOfflineSwalOpen) return;
  isOfflineSwalOpen = true;
  Swal.fire({
    title: "You're offline",
    text: 'Some features may be unavailable until your connection is restored.',
    icon: 'warning',
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    showCloseButton: false,
  }).then(() => {
    isOfflineSwalOpen = false;
  });
}

function initOfflineHandler(): void {
  if (!navigator.onLine) handleOffline();
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
}

function registerServiceWorker(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .catch((err: unknown) => {
        console.warn('Service worker registration failed:', err);
      });
  }
}

async function init(): Promise<void> {
  console.log(
    `%c AMWEST GAMING %c v${__BUILD_VERSION__} %c ${__BUILD_MODE__} %c ${__BUILD_DATE__} `,
    'background:#1a1a2e;color:#e94560;font-weight:bold;padding:2px 6px;',
    'background:#16213e;color:#0f3460;font-weight:bold;padding:2px 6px;',
    `background:${__BUILD_MODE__ === 'PROD' ? '#4caf50' : '#ff9800'};color:#fff;font-weight:bold;padding:2px 6px;`,
    'background:#333;color:#aaa;padding:2px 6px;',
  );

  registerServiceWorker();
  initOfflineHandler();

  const params = parseUrlParams();
  const gameConfig = getGameConfig(params.gameid);

  if (!gameConfig) {
    Swal.fire({
      title: 'Invalid Game ID',
      text: 'Could not find the game you are looking for. Please check the URL and try again.',
      icon: 'error',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });
    return;
  }

  await loadGame(gameConfig.name);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

window.addEventListener('message', (event: MessageEvent) => {
  if (event.data?.type === 'GAME_READY') {
    // placeholder — forward to parent or log
  }
});

window.GameRouter = {
  parseUrlParams,
  getGameConfig,
  loadGame,
  GAME_DIRECTORIES,
};
